import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Lightbox from '@components/shared/Lightbox/Lightbox';
import BeforeAfterSlider from '@components/shared/BeforeAfterSlider/BeforeAfterSlider';
import Modal from '@components/shared/Modal/Modal';
import styles from './Portfolio.module.css';

const Portfolio: React.FC = () => {
  const { config } = useConfig();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState<Array<{ src: string; alt?: string }>>([]);
  const [beforeAfterModalOpen, setBeforeAfterModalOpen] = useState(false);
  const [selectedBeforeAfter, setSelectedBeforeAfter] = useState<{ before: string; after: string } | null>(null);

  if (!config || !config.components.portfolio.enabled) return null;

  const { portfolio } = config.components;

  if (!portfolio.items || portfolio.items.length === 0) {
    return null;
  }

  const filteredItems = selectedCategory === 'All'
    ? portfolio.items
    : portfolio.items.filter(item => item.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.3,
      },
    },
  };

  const handleImageClick = (item: typeof portfolio.items[0]) => {
    if (item.beforeAfter?.enabled) {
      setSelectedBeforeAfter({
        before: item.beforeAfter.before,
        after: item.beforeAfter.after,
      });
      setBeforeAfterModalOpen(true);
    } else if (item.gallery && item.gallery.length > 0) {
      setSelectedImages(item.gallery.map(src => ({ src, alt: item.title })));
      setCurrentImageIndex(0);
      setLightboxOpen(true);
    } else {
      setSelectedImages([{ src: item.image, alt: item.title }]);
      setCurrentImageIndex(0);
      setLightboxOpen(true);
    }
  };

  return (
    <section id="portfolio" className={styles.portfolio}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          {/* Section Header */}
          <motion.div className={styles.header} variants={itemVariants}>
            <h2 className={styles.title}>Our Portfolio</h2>
            <div className={styles.divider}></div>
            <p className={styles.subtitle}>
              Explore our recent projects and see the quality of our work
            </p>
          </motion.div>

          {/* Category Filter */}
          {portfolio.categories.length > 1 && (
            <motion.div className={styles.filters} variants={itemVariants}>
              {portfolio.categories.map((category) => (
                <button
                  key={category}
                  className={`${styles.filterButton} ${
                    selectedCategory === category ? styles.active : ''
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          )}

          {/* Portfolio Grid */}
          <motion.div
            className={styles.grid}
            layout
          >
            <AnimatePresence mode="wait">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={`${item.title}-${index}`}
                  className={styles.portfolioItem}
                  variants={itemVariants}
                  layout
                  onClick={() => handleImageClick(item)}
                >
                  <div className={styles.imageWrapper}>
                    <img src={item.image} alt={item.title} className={styles.image} />
                    <div className={styles.overlay}>
                      <div className={styles.overlayContent}>
                        <h3 className={styles.itemTitle}>{item.title}</h3>
                        <p className={styles.itemDescription}>{item.description}</p>
                        <div className={styles.viewIcon}>
                          <i className="fas fa-search-plus"></i>
                        </div>
                      </div>
                    </div>
                    {item.beforeAfter?.enabled && (
                      <div className={styles.badge}>
                        <i className="fas fa-exchange-alt"></i> Before/After
                      </div>
                    )}
                    {item.gallery && item.gallery.length > 1 && (
                      <div className={styles.badge}>
                        <i className="fas fa-images"></i> {item.gallery.length} Photos
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          images={selectedImages}
          currentIndex={currentImageIndex}
        />
      )}

      {/* Before/After Modal */}
      {beforeAfterModalOpen && selectedBeforeAfter && (
        <Modal
          isOpen={beforeAfterModalOpen}
          onClose={() => setBeforeAfterModalOpen(false)}
          title="Before & After"
          size="xl"
        >
          <BeforeAfterSlider
            beforeImage={selectedBeforeAfter.before}
            afterImage={selectedBeforeAfter.after}
            beforeLabel="Before"
            afterLabel="After"
          />
        </Modal>
      )}
    </section>
  );
};

export default Portfolio;